<template>
  <div class="review-sidebar-list">
    <ReviewShowcaseTitle
      :average-rating="averageRating"
      :review-count="reviewCount"
    />
    <template v-for="review in reviews" :key="review.id">
      <div
        class="item"
        :class="{
          hover: isReviewHasDetail(review),
        }"
        @click="clickItem(review)"
      >
        <div class="flex justify-between items-start mb-3 gap-3">
          <div class="name">{{ review.customer }}</div>
          <ReviewVerifiedBadge v-if="review?.verified" />
        </div>
        <div v-if="review.media?.data?.length" class="flex gap-2">
          <img
            v-for="img in review.media?.data"
            :key="img.id"
            :src="getImageUrl(img)"
            class="w-[80px] h-[80px] object-cover rounded-lg"
            alt=""
          />
        </div>

        <div class="review-text">
          <div class="text" v-html="review.review" />
          <div v-if="isReviewHasDetail(review)" class="read-more">
            {{ t('general.kftReadMore.readMoreLabel') }}
          </div>
        </div>

        <div v-if="review.created_at" class="date">
          {{
            formatDate(
              review.created_at,
              $i18n.locale.toString(),
              'MMMM DD, YYYY'
            )
          }}
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '~/utils/dateHelpers'
import type { Review } from '~/composables/types/event'

interface Props {
  reviews: Review[]
  averageRating: string
  reviewCount: number
}

withDefaults(defineProps<Props>(), {
  reviews: () => [],
  averageRating: '',
  reviewCount: 0,
})

const $i18n = useI18n()
const { t } = useI18n()

const emit = defineEmits(['click'])

const clickItem = (review: Review): void => {
  if (isReviewHasDetail(review)) {
    emit('click', review.id)
  }
}

const isReviewHasDetail = (review: Review): boolean => {
  return !!review?.media?.data?.length
}

const getImageUrl = (image) => {
  if (image.url.includes('.mp4')) return '/_nuxt3-static/reviews/icon-play.png'

  return image.thumb || image.cdn_url || image.url
}
</script>

<style lang="postcss" scoped>
.review-sidebar-list {
  @apply flex flex-col gap-4;
}

.item {
  @apply border-stone-200 border rounded-xl p-2.5 ease-in-out duration-300 bg-white;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  &.hover {
    @apply cursor-pointer;

    &:hover {
      @apply border-stone-400;
    }
  }
}

.name {
  @apply text-base font-medium;
}

.read-more,
.text {
  @apply text-stone-500 text-base;
}

.read-more {
  @apply underline;
}

.date {
  @apply mt-3 text-xs text-gray-500;
}
</style>
